import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import Match2LiveOpsProgressbar from 'src/game/minigame/match2-timestep/components/Match2LiveOpsProgresbar';
import i18n from 'src/lib/i18n/i18n';
import { SuperLollipopIcon } from './SuperLollipopIcon';
import { waitForItPromise } from 'src/lib/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import gsap from 'gsap';
import { downscale } from 'src/conf/scale';
import { SuperLollipopSadPets } from './SuperLollipopSadPets';

type SuperLollipopLosingOpts = ViewOpts & {
  currentProgress: number;
  maxProgress: number;
};

export class SuperLollipopLosing extends View {
  private sadPets: SuperLollipopSadPets;
  private progressBar: Match2LiveOpsProgressbar;
  private icon: SuperLollipopIcon;
  private currentProgress = 0;
  private breakLoopAnimation = false;
  private lossAmount: LangBitmapFontTextView;
  private xIcon: ImageScaleView;
  private losingFullStreak = false;

  constructor(opts: SuperLollipopLosingOpts) {
    super({
      width: 540,
      height: 220,
      ...opts,
    });

    this.currentProgress = Math.min(opts.currentProgress, opts.maxProgress);
    this.losingFullStreak = this.currentProgress >= opts.maxProgress;

    this.sadPets = new SuperLollipopSadPets({
      superview: this,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5 - 50,
      centerOnOrigin: true,
      scale: 0.75,
      visible: this.losingFullStreak,
    });

    this.progressBar = new Match2LiveOpsProgressbar({
      superview: this,
      currentProgress: this.currentProgress,
      maxProgress: opts.maxProgress,
      title: this.losingFullStreak ? ' ' : i18n('superLollipop.title'),
      minWidth: 100, // This way the bar wouldn't be hidden behind the icon
    });

    this.progressBar.updateOpts({
      y: this.losingFullStreak ? 70 : 40,
    });

    this.icon = new SuperLollipopIcon({
      superview: this,
      x: 80,
      y: this.progressBar.style.y + 77,
    });

    this.xIcon = new ImageScaleView({
      superview: this.icon,
      image: 'assets/ui/superLollipop/shared/icon_x.png',
      x: 70,
      y: 70,
      width: 60,
      height: 60,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    this.lossAmount = new LangBitmapFontTextView({
      superview: this,
      size: 40,
      font: 'Title',
      color: '#FF4144',
      localeText: () => `-${this.currentProgress}`,
      width: 60,
      height: 60,
      x: 0,
      y: -5,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      visible: false, // WIll show up with the animation
    });

    // Break the animation loop when the view is removed
    this.on('ViewRemoved', () => {
      this.breakLoopAnimation = true;
    });
  }

  public animate() {
    this.loopAnimation();
  }

  private async loopAnimation() {
    if (this.breakLoopAnimation) return;
    this.progressBar.setProgress(this.currentProgress);
    await waitForItPromise(500);
    this.animateLossAmount();
    await waitForItPromise(500);
    if (this.losingFullStreak) {
      // Animate both bars if losing full streak, to make it more clear
      this.progressBar.greenProgressBar.animateProgress(0, 1500);
      await waitForItPromise(300);
      await this.progressBar.greyProgressBar.animateProgress(0, 1500);
    } else {
      // Otherwise just do the regular green bar animation
      await this.progressBar.greenProgressBar.animateProgress(0, 1500);
    }
    await waitForItPromise(1000);
    this.loopAnimation();
  }

  private async animateLossAmount() {
    const duration = 2;
    const startY = this.progressBar.style.y + 80;
    this.lossAmount.updateOpts({
      x: this.style.width / 2 + 170,
      y: startY,
      opacity: 1,
      scale: 0,
      visible: true,
    });
    gsap.to(this.lossAmount.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });
    gsap.to(this.lossAmount.style, {
      opacity: 0,
      duration: 0.3,
      ease: 'linear',
      delay: duration - 0.3,
    });
    await gsap.to(this.lossAmount.style, {
      y: startY - 80,
      duration: duration,
      ease: 'linear',
    });
  }

  public tick(dt: number) {
    const now = Date.now();

    this.xIcon.updateOpts({
      scale: 0.8 + Math.sin(now * 0.005) * 0.2,
    });
  }
}
